// Zwift Ride / Play / Click controllers (reverse-engineered protocol, experimental).
import { t } from '../i18n/index.js';
import { ZW_SVC, ZW_SVC16 } from './discovery.js';
const ZW_ASYNC = '00000002-19ca-4651-86e5-fa29dcdd09d1';
const ZW_SYNC_RX = '00000003-19ca-4651-86e5-fa29dcdd09d1';
const ZW_SYNC_TX = '00000004-19ca-4651-86e5-fa29dcdd09d1';
const RIDE_ON = [0x52, 0x69, 0x64, 0x65, 0x4f, 0x6e];

// Zwift Ride button mask (bit at 0 = pressed), based on open-source projects that decoded the protocol.
// Labels of known bits: t.devices.buttons.
export const DEFAULT_RIDE_MAP = {
  bit0: 'steerLeft', bit2: 'steerRight', bit1: 'fire', bit4: 'fire', bit11: 'fire', bit16: 'fire',
  bit9: 'gearDown', bit10: 'gearDown', bit13: 'gearUp', bit14: 'gearUp', bit3: 'look',
};

function readVarint(b, o) {
  let x = 0, sh = 0, c;
  do { if (o >= b.length) return null; c = b[o++]; x += (c & 0x7f) * 2 ** sh; sh += 7; } while (c & 0x80 && sh < 56);
  return [x, o];
}
// Minimal protobuf decoding: returns {field number: varint value}
function protoVarints(b, o) {
  const out = {};
  while (o < b.length) {
    const k = readVarint(b, o); if (!k) break; o = k[1];
    const f = Math.floor(k[0] / 8), wt = k[0] & 7;
    if (wt === 0) { const v = readVarint(b, o); if (!v) break; o = v[1]; if (!(f in out)) out[f] = v[0]; }
    else if (wt === 2) { const n = readVarint(b, o); if (!n) break; o = n[1] + n[0]; }
    else if (wt === 5) o += 4; else if (wt === 1) o += 8; else break;
  }
  return out;
}

export class ZwiftController {
  devs: any; onButton: any; onRaw: any; onStatus: any; pressed: any; seen: any;
  constructor(onButton, onStatus, onRaw) {
    this.onButton = onButton; this.onStatus = onStatus; this.onRaw = onRaw;
    this.pressed = new Set(); this.devs = [];
  }
  get connected() { return this.devs.some(d => d.gatt.connected); }

  async connect() {
    if (!navigator.bluetooth) throw new Error(t.devices.noBluetoothShort);
    const dev = await navigator.bluetooth.requestDevice({
      filters: [{ namePrefix: 'Zwift' }, { services: [ZW_SVC] }, { services: [ZW_SVC16] }],
      optionalServices: [ZW_SVC, ZW_SVC16, 'battery_service', 'device_information'],
    });
    await this.adopt(dev);
    return dev;
  }

  /** Connects a device already authorised by the browser. Left and right controllers add up; the same one replaces itself. */
  async adopt(dev) {
    this.devs = this.devs.filter(d => d.id !== dev.id);
    dev.addEventListener('gattserverdisconnected', () => this.onStatus(() => t.devices.nameDisconnected(dev.name), 'bad'));
    this.onStatus(() => t.devices.connecting);
    const srv = await dev.gatt.connect();
    let svc;
    try { svc = await srv.getPrimaryService(ZW_SVC16); } catch (e) { svc = await srv.getPrimaryService(ZW_SVC); }
    const asyncC = await svc.getCharacteristic(ZW_ASYNC);
    const rx = await svc.getCharacteristic(ZW_SYNC_RX);
    let tx = null; try { tx = await svc.getCharacteristic(ZW_SYNC_TX); } catch (e) {}
    const tag = dev.name || 'Zwift';
    asyncC.addEventListener('characteristicvaluechanged', e => this.parse(new Uint8Array(e.target.value.buffer), tag));
    await asyncC.startNotifications();
    if (tx) { tx.addEventListener('characteristicvaluechanged', e => this.onRaw?.(tag + ' sync', new Uint8Array(e.target.value.buffer))); await tx.startNotifications(); }
    await rx.writeValueWithResponse(new Uint8Array(RIDE_ON));
    this.devs.push(dev);
    const names = this.devs.map(d => d.name).join(' + ');
    this.onStatus(() => t.devices.pressToTest(names), 'ok');
  }

  parse(b, tag) {
    this.onRaw?.(tag, b);
    if (b.length < 2) return;
    const op = b[0];
    const f = protoVarints(b, 1);
    const now = new Set();
    if (op === 0x23 && f[1] != null) {
      // Zwift Ride: a mask where each bit at 0 signals a pressed button
      // a bit only counts as a button after having been seen released (at 1) at least once
      const m = f[1];
      this.seen = this.seen || new Set();
      for (let i = 0; i < 32; i++) { const on = Math.floor(m / 2 ** i) & 1; if (on) this.seen.add(i); else if (this.seen.has(i)) now.add('bit' + i); }
    } else if (op === 0x07 || op === 0x37) {
      // Zwift Play / Click: one field per button, 0 = pressed
      for (const k in f) if (f[k] === 0) now.add(`${tag.includes('Click') ? 'click' : 'play'}${op}_f${k}`);
    } else return;
    for (const k of now) if (!this.pressed.has(k)) this.onButton(k, true);
    for (const k of this.pressed) if (!now.has(k)) this.onButton(k, false);
    this.pressed = now;
  }

  disconnect() { for (const d of this.devs) d.gatt.disconnect(); this.devs = []; }
}

export function buttonLabel(key) {
  const m = /^bit(\d+)$/.exec(key);
  if (m) return t.devices.buttons[+m[1]] || t.devices.button(m[1]);
  return key;
}
